import { get, keyBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { COA_ACCOUNT_TYPE_COLUMN_NAME, COLUMN_GROUP_ORGANIZE_BY_INDEX } from '../../constants';
import { DeepPartial, createRecord, withDefaults } from '../../record';
import { EReportColumnTypes, IDimensionalDateReportColumn, IStorageReportColumn } from '../../reportColumns-immer';
import {
  IReportDatasetLens,
  IReportDatasetLensResponse,
  IReportDimension,
  IReportDimensionColumn
} from '../../reportDataset';
import { EReportSegmentUrns } from '../../standardTemplatePrompts';
import { createAccountSummaryChart } from '../accountSummaryCharts';
import { createMhDetailsViewConfig } from '../multiHierarchyDetails';
import {
  IDimensionColumn,
  IDimensionColumnReference,
  ILensColumnReference,
  IProjection,
  IStringConstantExpression,
  isSegmentScopedColumnReference
} from '../accountSummaryColumns';

import { ECalendarSemanticTypes } from '../../columnSemanticTypes';
import { buildColumnPathKey } from './columnPathKey';
import {
  COLUMN_LEVEL_SEPARATOR,
  columnValueLevelCount,
  extractColumnValueIds,
  idSlot,
  labelSlot
} from './columnValueSlots';
import {
  AggregationType,
  ColumnOperandIdentifier,
  ColumnValue,
  ColumnValueSegment,
  DEFAULT_MULTI_HIERARCHY_CODE_DISPLAY,
  DEFAULT_MULTI_HIERARCHY_DEPTH,
  EAccountSummaryRoundingOptions,
  EAccountSummaryVariant,
  EAllLedgerTypeDisplay,
  EGroupType,
  ELedgerType,
  ELedgerTypeDisplay,
  EZeroNumbersCheckboxOptions,
  HiddenColumnGroupIndicator,
  IAccountSummary,
  IAccountSummaryRounding,
  IAccountSummaryRoundingRule,
  IColumnAggregation,
  IColumnDataSource,
  IColumnSource,
  IGroupExpression,
  IGroupingSet,
  IRowGroup,
  IRowGroupLike,
  IRowOverride,
  IRowSource,
  MultiHierarchyAccountSummary,
  MultiHierarchyCalculatedColumnGroup,
  MultiHierarchyCalculatedRowGroup,
  MultiHierarchyColumnDataSource,
  MultiHierarchyCodeDisplayOption,
  MultiHierarchyDepthOption,
  MultiHierarchyGroupingLevel,
  MultiHierarchyRowGroup,
  MultiHierarchyRowOverride,
  OperandIdentifier,
  UnifiedLedgerType
} from '../base';

/**
 * A limited implementation to be able to distinguish IDimensionColumnReferences from other IProjection types.
 *
 * This is not valid for invalid IProjections.
 *
 * @param projection The projection being checked.
 */
export function isDimensionColumnReference(projection: IProjection): projection is IDimensionColumnReference {
  return !!(projection as IDimensionColumnReference)?.joinColumnName;
}

export function isLensColumnReference(projection: IProjection): projection is ILensColumnReference {
  return !isDimensionColumnReference(projection) && !!(projection as ILensColumnReference)?.columnName;
}

export const isStringConstantExpression = (column: IProjection): column is IStringConstantExpression =>
  (column as IStringConstantExpression)?.constant === '' || !!(column as IStringConstantExpression)?.constant;

export const isDimensionColumn = (column: IProjection): column is IDimensionColumn =>
  !!(column as IDimensionColumn)?.dimension;

export function isDisplayDelegateDimensionColumn(column: IReportDimensionColumn): boolean {
  return !!column.semanticTypes.find(isDisplayDelegate);
}

export function isAccounting(semanticType: string): boolean {
  return semanticType.includes('/semantic/accounting/coa');
}

export function isSemanticAccountingCoaDimensionColumn(column: IReportDimensionColumn): boolean {
  return Boolean(column.semanticTypes.find(isAccounting));
}

export function getDisplayDelegateColumn(lens: IReportDatasetLens): IReportDimensionColumn | undefined {
  if (!lens) return;

  const joinColSemanticType = lens.columns
    .find((col) => isAccountNumberDimensionColumn(col) || isAccountNumberIdDimensionColumn(col))
    ?.semanticTypes.find((semanticType) =>
      [EReportSegmentUrns.accountNumber, EReportSegmentUrns.accountNumberId].includes(
        semanticType as EReportSegmentUrns
      )
    );

  return lens.dimensions
    ?.find(
      (dim) => dim.id.includes('/coa/') && dim.columns.find((col) => col.semanticTypes.includes(joinColSemanticType))
    )
    ?.columns.find(isDisplayDelegateDimensionColumn);
}

interface SemanticObject {
  semanticTypes?: string[];
}

export function isDisplayDelegate(semanticType: string): boolean {
  return semanticType.startsWith('urn:opengov:types/semantic/display-delegate');
}

export function parseDisplayDelegateSource(displayDelegateColumn?: SemanticObject): string | undefined {
  const semanticType = displayDelegateColumn?.semanticTypes?.find(isDisplayDelegate);
  if (!semanticType) {
    return;
  }
  const params = new URLSearchParams(semanticType.split('?').pop());
  return params.get('column');
}

function hasSemanticType<T extends { semanticTypes: U[] }, U>(semanticType: U) {
  return (obj: T): boolean => obj.semanticTypes.includes(semanticType);
}

export const isAccountNumberIdDimensionColumn = hasSemanticType<IReportDimensionColumn, string>(
  EReportSegmentUrns.accountNumberId
);

export const isAccountNumberDimensionColumn = hasSemanticType<IReportDimensionColumn, string>(
  EReportSegmentUrns.accountNumber
);

const dimensionalJoinSemanticTypes = [
  'urn:opengov:types/semantic/accounting/coa/account-number',
  'urn:opengov:types/semantic/accounting/coa/account-number-ebto',
  'urn:opengov:types/semantic/accounting/coa/account-number-id',
  'urn:opengov:types/semantic/accounting/coa/department-code',
  'urn:opengov:types/semantic/accounting/coa/fund-code',
  'urn:opengov:types/semantic/accounting/coa/object-code',
  'urn:opengov:types/semantic/calendar/date',
  ECalendarSemanticTypes.FISCAL_YEAR_PERIOD
];

export const isDimensionalJoinSemanticType = (semanticType: string): boolean => {
  return dimensionalJoinSemanticTypes.includes(semanticType);
};

export const isV3CoaLens = hasSemanticType<IReportDatasetLensResponse, string>(EReportSegmentUrns.accountNumberId);

export const isV2CoaLens = hasSemanticType<IReportDatasetLensResponse, string>(EReportSegmentUrns.accountNumber);

export const isEbtoCoaLens = hasSemanticType<IReportDatasetLensResponse, string>(EReportSegmentUrns.accountNumberEbto);

export const isColumnDataSource = (columnSource: IColumnSource): columnSource is IColumnDataSource => {
  const { type } = columnSource;
  if (type === EGroupType.Lens) {
    return true;
  } else if (type === EGroupType.Expression) {
    return false;
  } else if (type === EGroupType.MultiHierarchy) {
    return false;
  } else if (type === EGroupType.MultiHierarchyCalculatedColumn) {
    return false;
  } else if (type === undefined) {
    return true;
  } else {
    throw new Error(`Unexpected type value "${type}" used for IColumnSource.type`);
  }
};

export function isMultiHierarchyVariant(variant: EAccountSummaryVariant | undefined): boolean {
  return variant === EAccountSummaryVariant.MultiHierarchy;
}

export function isMultiHierarchyAccountSummary(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary
): accountSummary is MultiHierarchyAccountSummary {
  return isMultiHierarchyVariant(accountSummary?.variant);
}

export function isMultiHierarchyColumnDataSource(
  columnSource: IColumnSource
): columnSource is MultiHierarchyColumnDataSource {
  return columnSource?.type === EGroupType.MultiHierarchy;
}

export function isMultiHierarchyRowGroup(rowSource: IRowSource | IRowGroupLike): rowSource is MultiHierarchyRowGroup {
  return rowSource?.type === EGroupType.MultiHierarchy;
}

/**
 * An ERP (multi-hierarchy) override row. Note this is intentionally NOT matched by
 * `isRowOverride`, which only recognises the non-ERP `EGroupType.Override`.
 */
export function isMultiHierarchyRowOverride(
  rowSource: IRowSource | IRowGroupLike
): rowSource is MultiHierarchyRowOverride {
  return rowSource?.type === EGroupType.MultiHierarchyOverride;
}

/**
 * True for either flavour of multi-hierarchy row group — the plain row group or the
 * override row. Both carry `displayName` and `grouping`.
 *
 * Exported because consumers kept reimplementing it: `isRollupRowSource`,
 * `isSummaryTableDataRowGroup`, `isMultiHierarchyDataRowSource`, `isChartableMhRowGroup` and others
 * were each `isMultiHierarchyRowGroup(x) || isMultiHierarchyRowOverride(x)` with their own doc
 * comment. Prefer this over hand-rolling the disjunction again — an `isMultiHierarchyRowGroup` check
 * on its own silently drops override rows, which is how most of the ERP override bugs started.
 */
export function isMultiHierarchyRowGroupLike(
  rowSource: IRowSource | IRowGroupLike | undefined
): rowSource is MultiHierarchyRowGroup | MultiHierarchyRowOverride {
  // Accepts `undefined` because most callers are indexing into `rowGroups` by an index that may be
  // out of range; both underlying guards already read `type` through `?.`.
  if (!rowSource) return false;
  return isMultiHierarchyRowGroup(rowSource) || isMultiHierarchyRowOverride(rowSource);
}

/**
 * A multi-hierarchy grouping level that breaks down by leaf Account Number.
 * Retains `segmentId`/`groupIds` for scope but enumerates `account_number_id`.
 */
export function isMultiHierarchyAccountNumberLevel(level: MultiHierarchyGroupingLevel | undefined): boolean {
  return level?.accountNumber === true;
}

/**
 * Whether `levelIdx` is the level whose `hierarchyDepth` / `codeDisplay` settings are honoured
 * (BUD-19598): the deepest configured level, and only when it is a CoA segment level (not a non-CoA
 * column, not Account Number) in a row group whose FIRST level is also CoA. A row group that starts
 * with a non-CoA column drills by path rather than by config level (a "mixed drill"), so neither the
 * code expand nor these settings can place its rows — the editor hides the controls there. Mirrors
 * `findCodeLeafLevel` plus the mixed-drill exclusion in the reporting-service rows pipeline.
 */
export function isMultiHierarchyCodeLeafLevel(
  grouping: ReadonlyArray<Pick<MultiHierarchyGroupingLevel, 'segmentId' | 'columnName' | 'accountNumber'>>,
  levelIdx: number
): boolean {
  const level = grouping[levelIdx];
  if (level === undefined || levelIdx !== grouping.length - 1) return false;
  const startsWithNonCoaColumn = Boolean(grouping[0]?.columnName);
  if (startsWithNonCoaColumn) return false;
  const isCoaSegmentLevel = Boolean(level.segmentId) && !level.columnName;
  return isCoaSegmentLevel && level.accountNumber !== true;
}

type LevelShape = Pick<MultiHierarchyGroupingLevel, 'segmentId' | 'columnName' | 'accountNumber'>;

const isCoaSegmentLevel = (level: LevelShape | undefined): boolean =>
  level !== undefined && Boolean(level.segmentId) && !level.columnName && level.accountNumber !== true;

/**
 * Whether `levelIdx` is a level whose `hierarchyDepth` is honoured (BUD-19598): any CoA segment
 * level — not Account Number, not a non-CoA column — of a row group whose FIRST level is CoA. A
 * row group that starts with a non-CoA column drills by path rather than by config level (a
 * "mixed drill"), so no setting can place its rows; the editor hides the controls there. Levels
 * after an Account Number level are never queried, so they carry no setting either.
 */
export function isMultiHierarchyDepthLevel(grouping: ReadonlyArray<LevelShape>, levelIdx: number): boolean {
  const level = grouping[levelIdx];
  if (level === undefined || Boolean(grouping[0]?.columnName)) return false;
  const accountNumberLevelIdx = grouping.findIndex((candidate) => candidate.accountNumber === true);
  const isBeyondAccountNumber = accountNumberLevelIdx >= 0 && levelIdx >= accountNumberLevelIdx;
  return !isBeyondAccountNumber && isCoaSegmentLevel(level);
}

/**
 * Whether `levelIdx` is the leaf-detail level: the depth level whose leaf groups expand into their
 * leaf detail — the deepest CoA segment level (its segment codes), or the CoA level directly above
 * an Account Number level (its accounts). The only level `hierarchyDepth: 'leaf'` applies to.
 */
export function isMultiHierarchyLeafDetailLevel(grouping: ReadonlyArray<LevelShape>, levelIdx: number): boolean {
  if (!isMultiHierarchyDepthLevel(grouping, levelIdx)) return false;
  const next = grouping[levelIdx + 1];
  return next === undefined || next.accountNumber === true;
}

/**
 * Whether `levelIdx` may drop its own group row (`hideGroupRow`): a leaf-detail level that is a
 * breakdown level (index ≥ 1), so a parent row exists for its leaf detail to list under. Level 1's
 * group rows are the row group's root rows and always render.
 */
export function isMultiHierarchyGroupRowHideable(grouping: ReadonlyArray<LevelShape>, levelIdx: number): boolean {
  return levelIdx >= 1 && isMultiHierarchyLeafDetailLevel(grouping, levelIdx);
}

export interface MultiHierarchyLevelDisplay {
  hierarchyDepth: MultiHierarchyDepthOption;
  codeDisplay: MultiHierarchyCodeDisplayOption;
  /** True only under `leaf`: the level's leaf detail lists directly under the parent row. */
  hideGroupRow: boolean;
}

/**
 * The effective Depth / Code display of a level, with defaults and the coercion rules applied:
 * `leaf` + `none` would leave nothing beneath the group, so Code display falls back to its default
 * (the UI blocks that combination; a hand-edited config gets the same answer), and `hideGroupRow`
 * only holds under `leaf` — a hidden group row with sub-groups or nothing beneath it would leave the
 * level unreadable. Under `rollup` the code display is returned as stored but is irrelevant — no
 * codes render.
 */
export function resolveMultiHierarchyLevelDisplay(
  level: Pick<MultiHierarchyGroupingLevel, 'hierarchyDepth' | 'codeDisplay' | 'hideGroupRow'> | undefined
): MultiHierarchyLevelDisplay {
  const hierarchyDepth = level?.hierarchyDepth ?? DEFAULT_MULTI_HIERARCHY_DEPTH;
  const storedCodeDisplay = level?.codeDisplay ?? DEFAULT_MULTI_HIERARCHY_CODE_DISPLAY;
  const codeDisplay =
    hierarchyDepth === 'leaf' && storedCodeDisplay === 'none'
      ? DEFAULT_MULTI_HIERARCHY_CODE_DISPLAY
      : storedCodeDisplay;
  const hideGroupRow = hierarchyDepth === 'leaf' && level?.hideGroupRow === true;
  return { hierarchyDepth, codeDisplay, hideGroupRow };
}

/**
 * The id the grid keys a multi-hierarchy row group's first root row by, when the group renders
 * several root rows; undefined for a single root or none.
 *
 * A first root that belongs to a merge is keyed by the merge id, since the merge renders as one row.
 */
export function getMultiHierarchyFirstRootRowId(rowGroup: {
  grouping?: MultiHierarchyGroupingLevel[];
}): string | undefined {
  const rootLevel = rowGroup.grouping?.[0];
  const rootGroupIds = rootLevel?.groupIds ?? [];
  if (rootGroupIds.length <= 1) {
    return undefined;
  }
  const firstRootGroupId = rootGroupIds[0];
  const owningMerge = rootLevel?.mergedGroups?.find((merge) => merge.groupIds.includes(firstRootGroupId));
  return owningMerge?.id ?? firstRootGroupId;
}

/**
 * The name of a multi-hierarchy row group as a whole. A group with several roots keeps that name on
 * its first root row (the row-level `displayName` would otherwise repeat on every root row), so it is
 * read from there when the row group carries none of its own.
 */
function getMultiHierarchyWholeGroupName(
  rowGroup: MultiHierarchyRowGroup | MultiHierarchyRowOverride
): string | undefined {
  if (rowGroup.displayName) {
    return rowGroup.displayName;
  }
  const firstRootRowId = getMultiHierarchyFirstRootRowId(rowGroup);
  return firstRootRowId ? rowGroup.groupDisplayNames?.[firstRootRowId] : undefined;
}

export function isColumnAggregation(columnSource: IColumnSource): columnSource is IColumnAggregation {
  if (
    !columnSource ||
    !('aggregation' in columnSource) ||
    !columnSource.aggregation ||
    columnSource.aggregation === AggregationType.NONE
  ) {
    return false;
  }
  const { aggregation } = columnSource;
  if (Object.values(AggregationType).includes(aggregation)) {
    return true;
  }
  throw new Error(`Invalid aggregration type ${aggregation} for column group: ${columnSource.id}`);
}

export function isRowOverride(rowSource: IRowSource | IRowGroupLike): rowSource is IRowOverride {
  return rowSource?.type === EGroupType.Override;
}

export function isRowDataSource(rowSource: IRowSource | IRowGroupLike): rowSource is IRowGroup {
  return !rowSource?.hasOwnProperty('type') || rowSource?.type === EGroupType.COA;
}

export function isCalculationExpression(groupSource: IColumnSource | IRowSource): groupSource is IGroupExpression {
  return groupSource?.type === EGroupType.Expression;
}

export function isMultiHierarchyCalculatedRowGroup(
  rowSource: IRowSource | IRowGroupLike
): rowSource is MultiHierarchyCalculatedRowGroup {
  return rowSource?.type === EGroupType.MultiHierarchyCalculated;
}

/** True for any of the three multi-hierarchy row source flavours. */
function isMultiHierarchyRowSource(
  rowSource: IRowSource | IRowGroupLike
): rowSource is MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup {
  return isMultiHierarchyRowGroupLike(rowSource) || isMultiHierarchyCalculatedRowGroup(rowSource);
}

export function isMultiHierarchyCalculatedColumnGroup(
  columnSource: IColumnSource | MultiHierarchyCalculatedColumnGroup
): columnSource is MultiHierarchyCalculatedColumnGroup {
  return columnSource?.type === EGroupType.MultiHierarchyCalculatedColumn;
}

export function isAccountTypeDataSource(columnSource: IColumnSource): columnSource is IColumnDataSource {
  return (
    isColumnDataSource(columnSource) &&
    (columnSource as IGroupingSet<IDimensionColumnReference>).grouping?.[1]?.columnName === COA_ACCOUNT_TYPE_COLUMN_NAME
  );
}

export const isLedgerType = (val: any): val is ELedgerType => {
  return val in ELedgerTypeDisplay;
};

/**
 * The `columnValueMapping` field key for one organize-by level, i.e. the level's own projection
 * key — its segmentId, or its lens column name.
 *
 * Renames are stored as `mapping[columnField][pathKey]`, so each level renames under its own
 * field. Level 1 reads the same projection it always has, which is what keeps saved renames
 * resolving without migration.
 */
export function getColumnValueMappingKeyForLevel(
  columnSource: IColumnDataSource | MultiHierarchyColumnDataSource,
  level = 1
): string | undefined {
  const projection = getOrganizeByProjections(columnSource as unknown as IColumnSource)[level - 1];
  if (!projection) return undefined;
  // Both segment and account-number column refs are segment-scoped and key their column-value
  // mapping by segmentId (account-number narrows the group-ref guard, so use the shared predicate).
  if (isSegmentScopedColumnReference(projection)) {
    return projection.segmentId !== undefined ? String(projection.segmentId) : undefined;
  }
  return (projection as ILensColumnReference)?.columnName;
}

export function getColumnValueMappingKey(
  columnSource: IColumnDataSource | MultiHierarchyColumnDataSource
): string | undefined {
  return getColumnValueMappingKeyForLevel(columnSource, 1);
}

/**
 * Resolves a column's rename.
 *
 * `value` is either a single segment (the legacy level-1 form) or the column's full level path.
 * A path is keyed by `buildColumnPathKey`, which returns a one-element path RAW — so a legacy
 * rename resolves identically whether the caller passes `'100'` or `['100']`.
 *
 * `level` must match the depth `value` describes, because it selects which projection's field the
 * rename lives under. A level-2 rename therefore resolves only under its own parent: a sibling
 * parent sharing the leaf id produces a different path key.
 */
export const getColumnValueMapping = (
  columnSource: IColumnDataSource | MultiHierarchyColumnDataSource,
  value: ColumnValueSegment | ColumnValueSegment[],
  level = 1
): string | undefined => {
  if (value === undefined) return undefined;
  // Callers resolve the column source by id, which can come up empty when a chart or saved layout
  // still references a deleted column group.
  if (!columnSource?.columnValueMapping) return undefined;
  const columnField = getColumnValueMappingKeyForLevel(columnSource, level);
  // A bare segment is just a one-element path, so buildColumnPathKey stays the single place that
  // normalizes a null segment to the totals key.
  const lookupKey = buildColumnPathKey(Array.isArray(value) ? value : [value]);
  return get(columnSource.columnValueMapping, [columnField, lookupKey]) as string | undefined;
};

export const isUnifiedLedgerType = (val: unknown): val is UnifiedLedgerType => {
  return typeof val === 'string' && (val in EAllLedgerTypeDisplay || val in ELedgerTypeDisplay);
};

export function getColumnGroupName(group: IColumnSource): string {
  if (isCalculationExpression(group) || isMultiHierarchyCalculatedColumnGroup(group)) {
    return group.alias;
  }
  return (group as IGroupingSet<IStringConstantExpression>)?.grouping?.[0]?.constant;
}

export function getRowGroupName(rowGroup: IRowSource): string {
  if (!rowGroup) return '';
  if (rowGroup.type === EGroupType.Expression) {
    return (rowGroup as IGroupExpression).alias;
  }
  if (isMultiHierarchyRowGroupLike(rowGroup)) {
    return rowGroup.displayName ?? '';
  }
  return (rowGroup as IRowGroup).groupName;
}

/**
 * Returns a display-friendly label for a row group, supporting standard Account Summary
 * (IRowGroup) row groups as well as both Multi-Hierarchy flavours (MultiHierarchyRowGroup
 * and MultiHierarchyRowOverride).
 *
 * Resolution order:
 *  1. MH `displayName` (when present on either MH row group flavour)
 *  2. MH first root row's name in `groupDisplayNames` (only when the group has several roots)
 *  3. AS `groupName` (when present on IRowGroup)
 *  4. Auto-generated fallback label ("Row Group {index}")
 */
export function getRowGroupDisplayName(
  rowGroup: IRowGroup | MultiHierarchyRowGroup | MultiHierarchyRowOverride,
  index?: number
): string {
  if (!rowGroup) return '';

  const multiHierarchyName = isMultiHierarchyRowGroupLike(rowGroup)
    ? getMultiHierarchyWholeGroupName(rowGroup)
    : undefined;
  if (multiHierarchyName) {
    return multiHierarchyName;
  }

  if (isRowDataSource(rowGroup)) {
    return rowGroup.groupName || `Row Group ${(index ?? 0) + 1}`;
  }

  return `Row Group ${(index ?? 0) + 1}`;
}

/**
 * Get the display label for a row group, similar to getColumnLabel for columns.
 * Handles both parent row groups and child row values.
 *
 * @param rowGroup - The row group configuration object
 * @param childValue - The child row value (when drilling down into a row group)
 * @param formattedChildValue - The formatted/display value for the child
 * @returns The display label for the row
 */
export function getRowLabel(rowGroup: IRowSource, childValue?: string | null, formattedChildValue?: string): string {
  const rowGroupName = getRowGroupName(rowGroup);

  // If it's a calculation expression, just return the alias
  if (!rowGroup || rowGroup.type === EGroupType.Expression) {
    return rowGroupName;
  }

  // If there's a child value, use it (for drilled-down rows)
  if (childValue) {
    // Use formatted value if provided, otherwise use the raw value
    return formattedChildValue || childValue;
  }

  // For parent rows, just return the group name
  return rowGroupName;
}

/**
 * Generate a row label combining group name and child value.
 * Similar to generateColumnLabel but for rows.
 *
 * @param rowGroupName - The row group name
 * @param childValue - The child row value
 * @returns Combined label
 */
export function generateRowLabel(rowGroupName?: string | null, childValue?: string | null): string {
  const trimmedGroupName = rowGroupName?.trim();
  const trimmedValue = childValue?.trim();
  const hasGroup = !!trimmedGroupName;
  const hasValue = !!trimmedValue;

  if (hasGroup && hasValue) return `${trimmedGroupName} - ${trimmedValue}`;
  if (hasGroup) return trimmedGroupName;
  if (hasValue) return trimmedValue;
  return '';
}

// Get last override or coa row group (not calculated)
export function getLastRowGroup(
  rowGroups: IRowSource[],
  rowGroupIndex?: number
): Exclude<IRowSource, IGroupExpression> {
  const index = rowGroupIndex ?? rowGroups.length - 1;
  for (let ind = index; ind >= 0; ind--) {
    if (!isCalculationExpression(rowGroups[ind])) {
      return rowGroups[ind] as Exclude<IRowSource, IGroupExpression>;
    }
  }
}

export const getLastRowGroupAsCoA: (rowGroups: IRowSource[], rowGroupIndex: number | null) => IRowGroup = (
  rowGroups,
  rowGroupIndex
) => {
  const rowGroup = getLastRowGroup(rowGroups, rowGroupIndex);
  if (isMultiHierarchyRowSource(rowGroup)) {
    throw new Error(`getLastRowGroupAsCoA cannot be called with a ${rowGroup.type} row group`);
  }
  if (isRowOverride(rowGroup)) {
    const { columnGroups, ...coaRowGroup } = { ...rowGroup, type: EGroupType.COA };
    return coaRowGroup as IRowGroup;
  }
  return rowGroup;
};

export function getLastRowGroupAsOverride(rowGroups: IRowSource[], rowGroupIndex?: number): IRowOverride {
  const rowGroup = getLastRowGroup(rowGroups, rowGroupIndex);
  return { ...rowGroup, type: EGroupType.Override, columnGroups: {} } as unknown as IRowOverride;
}

const accountSummaryRecord = createRecord<IAccountSummary>({
  columnGroups: [],
  columnOrder: {},
  expandedGroupKeys: {},
  expandedRows: {},
  filters: [],
  formatting: {
    currencyRounding: EAccountSummaryRoundingOptions.Ones,
    numberRounding: EAccountSummaryRoundingOptions.Ones,
    percentRounding: EAccountSummaryRoundingOptions.Ones,
    negativeNumberDisplay: EAccountSummaryRoundingOptions.Standard,
    zeroNumbers: [{ label: EZeroNumbersCheckboxOptions.Rows, enabled: false }]
  },
  hiddenColumns: {},
  rowGroups: [],
  charts: []
});

const multiHierarchyAccountSummaryRecord = createRecord<MultiHierarchyAccountSummary>({
  variant: EAccountSummaryVariant.MultiHierarchy,
  columnGroups: [],
  columnOrder: {},
  expandedGroupKeys: {},
  expandedRows: {},
  filters: [],
  hideColumnGroupHeaders: false,
  formatting: {
    currencyRounding: EAccountSummaryRoundingOptions.Ones,
    numberRounding: EAccountSummaryRoundingOptions.Ones,
    percentRounding: EAccountSummaryRoundingOptions.Ones,
    negativeNumberDisplay: EAccountSummaryRoundingOptions.Standard,
    zeroNumbers: [{ label: EZeroNumbersCheckboxOptions.Rows, enabled: false }]
  },
  hiddenColumns: {},
  rowGroups: [],
  charts: [],
  showTotalsRows: true,
  showRootLevelBreakdowns: false,
  // `createRecord` copies only the keys its defaults name, so `details` must be listed here or it
  // is silently dropped on every save. `undefined` allow-lists the key while keeping it out of the
  // serialized config until a user configures the Details tab.
  details: undefined,
  // Same allow-list pattern for public MH drill capabilities (BUD-18770).
  publicDrillCapabilities: undefined
});

const roundingRecord = createRecord<IAccountSummaryRounding>({
  scale: 4,
  rule: IAccountSummaryRoundingRule.RoundToNearest
});

const columnDataSourceRecord = (values) => {
  const result = withDefaults<IColumnDataSource>({
    id: undefined,
    lens: null,
    measureColumn: null,
    grouping: [],
    filters: [],
    dateFilter: [],
    relativeFilter: '',
    type: EGroupType.Lens,
    columnValueMapping: {},
    rollingNumRangeOption: {
      value: '',
      label: ''
    }
  })(values);

  if ((result.type as string) === 'datasource') {
    result.type = EGroupType.Lens;
  }
  return result;
};

const columnAggregationRecord = (values) => {
  const result = withDefaults<IColumnAggregation>({
    id: undefined,
    lens: null,
    measureColumn: null,
    grouping: [],
    filters: [],
    dateFilter: [],
    relativeFilter: '',
    type: EGroupType.Lens,
    aggregation: AggregationType.NONE,
    columnValueMapping: {},
    rollingNumRangeOption: {
      value: '',
      label: ''
    }
  })(values);

  if ((result.type as string) === 'datasource') {
    result.type = EGroupType.Lens;
  }
  return result;
};

const multiHierarchyColumnDataSourceRecord = (values) => {
  const result = withDefaults<MultiHierarchyColumnDataSource>({
    id: undefined,
    lens: null,
    measureColumn: null,
    grouping: [],
    type: EGroupType.MultiHierarchy,
    filters: [],
    showTotalsColumn: false,
    columnValueMapping: {},
    dateFilter: [],
    relativeFilter: '',
    relativeRolling: '',
    rollingNumRangeOption: {
      value: '',
      label: ''
    }
  })(values);

  return result;
};

const createCalculationGroupExpression = <Operand extends OperandIdentifier = OperandIdentifier>(values) => {
  const result = withDefaults<IGroupExpression<Operand>>({
    id: undefined,
    expression: undefined,
    alias: undefined,
    calculation: undefined,
    display: undefined,
    operands: {},
    type: EGroupType.Expression
  })(values);

  // migrate columnAlias to alias
  if (result.columnAlias) {
    result.alias = result.columnAlias;
    delete result.columnAlias;
  }

  function isArray<T>(operand: T | T[]): operand is T[] {
    return Array.isArray(operand);
  }
  // migrate operands to include column order
  Object.values(result.operands).forEach((operand: Operand) => {
    if (isArray<OperandIdentifier>(operand) && operand.length < 3) {
      operand.push(operand[operand.length - 1]);
    }
  });

  return result;
};

const ensureUniqueId = (callback) => (record: DeepPartial<IRowSource> | DeepPartial<IColumnSource>) => {
  const result = callback(record);
  if (!result.id) {
    result.id = uuidv4();
  }
  return result;
};

const createMultiHierarchyCalculatedColumnGroup = withDefaults<MultiHierarchyCalculatedColumnGroup>({
  id: '',
  type: EGroupType.MultiHierarchyCalculatedColumn,
  formula: '',
  alias: '',
  display: undefined
});

const createColumnGroup = (columnGroup: IColumnSource): IColumnSource => {
  if (!columnGroup) return;
  if (columnGroup.type === EGroupType.Expression) {
    return createCalculationGroupExpression<ColumnOperandIdentifier>(columnGroup);
  }

  if (isMultiHierarchyCalculatedColumnGroup(columnGroup)) {
    return createMultiHierarchyCalculatedColumnGroup(columnGroup as DeepPartial<MultiHierarchyCalculatedColumnGroup>);
  }

  if (isMultiHierarchyColumnDataSource(columnGroup)) {
    return multiHierarchyColumnDataSourceRecord(columnGroup);
  }

  if (isColumnAggregation(columnGroup)) {
    return columnAggregationRecord(columnGroup);
  }

  return columnDataSourceRecord(columnGroup);
};

export const createColumnGroups = (values: DeepPartial<IColumnSource>[]): IColumnSource[] =>
  values ? values.map(ensureUniqueId(createColumnGroup)) : [];

const createRowGroupCoa = withDefaults<IRowGroup>({
  id: undefined,
  type: EGroupType.COA,
  grouping: [],
  filters: [],
  showOnly: null,
  groupName: null
});

const createMultiHierarchyRowGroup = withDefaults<MultiHierarchyRowGroup>({
  id: undefined,
  displayName: undefined,
  groupDisplayNames: undefined,
  type: EGroupType.MultiHierarchy,
  grouping: [],
  filters: []
});

const createMultiHierarchyCalculatedRowGroup = withDefaults<MultiHierarchyCalculatedRowGroup>({
  id: '',
  type: EGroupType.MultiHierarchyCalculated,
  formula: '',
  alias: ''
});

function mapEntries<T>(obj: T, callback: (entry: [string, any], number) => [string, any]) {
  return Object.fromEntries(Object.entries(obj).map(callback));
}

const createRowGroupOverride = (values: DeepPartial<IRowOverride>): IRowOverride => ({
  ...createRowGroupCoa(values as unknown as IRowGroup),
  type: EGroupType.Override,
  columnGroups: values?.columnGroups
    ? mapEntries(values.columnGroups, ([key, value]) => [key, createColumnGroup(value)])
    : {}
});

const createMultiHierarchyRowOverride = (
  values: DeepPartial<MultiHierarchyRowOverride>
): MultiHierarchyRowOverride => ({
  ...createMultiHierarchyRowGroup(values as unknown as DeepPartial<MultiHierarchyRowGroup>),
  type: EGroupType.MultiHierarchyOverride,
  columnGroups: values?.columnGroups
    ? mapEntries(values.columnGroups, ([key, value]) => [key, createColumnGroup(value)])
    : {}
});

const createRowGroup = (rowGroup: DeepPartial<IRowSource>): IRowSource => {
  switch (rowGroup.type) {
    case EGroupType.Expression:
      return createCalculationGroupExpression(rowGroup);
    case EGroupType.Override:
      return createRowGroupOverride(rowGroup);
    case EGroupType.MultiHierarchy:
      return createMultiHierarchyRowGroup(rowGroup as DeepPartial<MultiHierarchyRowGroup>);
    case EGroupType.MultiHierarchyOverride:
      return createMultiHierarchyRowOverride(rowGroup as DeepPartial<MultiHierarchyRowOverride>);
    case EGroupType.MultiHierarchyCalculated:
      return createMultiHierarchyCalculatedRowGroup(rowGroup as DeepPartial<MultiHierarchyCalculatedRowGroup>);

    // the original rowGroupType was COA, so maintain backwards compatibility
    case undefined:
    case EGroupType.COA:
      return createRowGroupCoa(rowGroup);
    default:
      throw new Error(`Encountered unexpected rowGroup type for rowGroup: ${JSON.stringify(rowGroup)}`);
  }
};

export const createRowGroups = (values: DeepPartial<IRowSource>[]): IRowSource[] | MultiHierarchyRowGroup[] => {
  if (values) {
    return values.map(ensureUniqueId(createRowGroup));
  } else {
    return [ensureUniqueId(createRowGroupCoa)({})];
  }
};

type MultiHierarchyRowSource = MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup;

export const createMultiHierarchyRowGroups = (
  values: DeepPartial<MultiHierarchyRowSource>[]
): MultiHierarchyRowSource[] => {
  if (!values) {
    return [ensureUniqueId(createMultiHierarchyRowGroup)({})];
  }

  // Exhaustive rather than falling through to `createMultiHierarchyRowGroup`, which matches
  // `createRowGroup`'s behaviour. The fall-through quietly downgraded any unrecognised type to a plain
  // row group and dropped the fields that type carries — an override row lost its `columnGroups`, so
  // the report came back rendering the parent's numbers instead of the override's, with no error.
  return values.map((rowGroup) => {
    switch (rowGroup.type) {
      case EGroupType.MultiHierarchyCalculated:
        return createMultiHierarchyCalculatedRowGroup(rowGroup as DeepPartial<MultiHierarchyCalculatedRowGroup>);
      case EGroupType.MultiHierarchyOverride:
        return ensureUniqueId(createMultiHierarchyRowOverride)(rowGroup as DeepPartial<MultiHierarchyRowOverride>);
      // `undefined` is retained for backwards compatibility with configs written before the
      // discriminant existed, exactly as `createRowGroup` treats a missing type as COA.
      case undefined:
      case EGroupType.MultiHierarchy:
        return ensureUniqueId(createMultiHierarchyRowGroup)(rowGroup as DeepPartial<MultiHierarchyRowGroup>);
      default:
        throw new Error(
          `Encountered unexpected multi-hierarchy rowGroup type for rowGroup: ${JSON.stringify(rowGroup)}`
        );
    }
  });
};
export const createProjectionFromReportColumn = (
  columnsColumn: IDimensionalDateReportColumn | IStorageReportColumn
): IProjection => {
  if (columnsColumn.columnType === EReportColumnTypes.dimensional) {
    return {
      columnName: columnsColumn.dateColumnName,
      dimension: { id: columnsColumn.dimensionId },
      joinColumnName: columnsColumn.name
    };
  } else {
    return {
      columnName: columnsColumn.name
    };
  }
};

export const getShowOnlyColumn = (dimension: IReportDimension): IReportDimensionColumn => {
  for (const column of dimension.columns) {
    if (column.semanticTypes.includes(EReportSegmentUrns.accountType)) {
      return column;
    }
  }
  throw new Error(
    `Column with semantic type [${EReportSegmentUrns.accountType}] not found in dimension [${dimension.name}]`
  );
};

export const createAccountSummary = (values: DeepPartial<IAccountSummary> = {}): IAccountSummary => ({
  ...accountSummaryRecord(values),
  columnGroups: createColumnGroups(values?.columnGroups),
  rowGroups: createRowGroups(values?.rowGroups),
  asOfDate: values?.asOfDate,
  ...(values?.rounding && { rounding: roundingRecord(values?.rounding) }),
  charts: (values?.charts ?? []).map(createAccountSummaryChart),
  hideColumnGroupHeaders: values?.hideColumnGroupHeaders
});

export const createMultiHierarchyAccountSummary = (
  values: DeepPartial<MultiHierarchyAccountSummary> = {}
): MultiHierarchyAccountSummary => {
  const record = {
    ...multiHierarchyAccountSummaryRecord(values),
    columnGroups: createColumnGroups(
      values?.columnGroups as DeepPartial<MultiHierarchyColumnDataSource>[]
    ) as MultiHierarchyColumnDataSource[],
    rowGroups: createMultiHierarchyRowGroups(values?.rowGroups),
    asOfDate: values?.asOfDate,
    ...(values?.rounding && { rounding: roundingRecord(values?.rounding) }),
    ...(values?.details && { details: createMhDetailsViewConfig(values.details) }),
    hideColumnGroupHeaders: values?.hideColumnGroupHeaders
  };
  return record;
};

export function getColumnValuesByGroupId(columnValues: ColumnValueSegment[][]): Record<string, ColumnValue[]> {
  return columnValues.reduce<Record<string, ColumnValue[]>>((result, next: ColumnValue) => {
    result[next[0]] = result[next[0]] || [];
    result[next[0]].push(next);
    return result;
  }, {});
}

export function getColumnGroupsByGroupName(columnGroups: IColumnSource[]): Record<string, IColumnSource[]> {
  return columnGroups.reduce((acc, columnGroup) => {
    const groupName = getColumnGroupName(columnGroup);
    acc[groupName] = columnGroup;
    return acc;
  }, {});
}

/**
 * The lens column the group's columns are organized by, at its DEEPEST organize-by level.
 *
 * The deepest level is the one whose distinct values the group's columns actually enumerate, so it
 * is the level whose schema decides how a column is identified — the same rule
 * `getColumnSchemaForColumnGroup` follows. For a one-level group this is still `grouping[1]`.
 */
export function getDimensionColumnName(group: IColumnSource): string | null {
  if (isCalculationExpression(group) || isMultiHierarchyCalculatedColumnGroup(group)) return null;
  const projections = getOrganizeByProjections(group);
  const grouping = projections[projections.length - 1];
  if (isDimensionColumnReference(grouping)) return grouping.joinColumnName;
  if (isLensColumnReference(grouping)) return grouping.columnName;
  return null;
}

/**
 * The column group's organize-by levels, outermost first.
 *
 * Reads through `grouping.slice(COLUMN_GROUP_ORGANIZE_BY_INDEX)` rather than a fixed index, and
 * drops absent entries: a saved override column group can lack `grouping` entirely, and an
 * in-progress edit can leave a hole where a middle level was removed.
 */
export function getOrganizeByProjections(group: IColumnSource): IProjection[] {
  if (isCalculationExpression(group) || isMultiHierarchyCalculatedColumnGroup(group)) return [];
  const grouping = (group as { grouping?: ReadonlyArray<IProjection> }).grouping;
  if (!grouping) return [];
  return grouping
    .slice(COLUMN_GROUP_ORGANIZE_BY_INDEX)
    .filter((projection): projection is IProjection => projection !== undefined && projection !== null);
}

export function getLensIdsFromReport(report: IAccountSummary | MultiHierarchyAccountSummary) {
  const columnGroupGroups = [
    report.columnGroups,
    ...report.rowGroups.map((group) => {
      // Both the non-ERP override row and the ERP (multi-hierarchy) override row carry
      // per-column data sources whose lenses must be prefetched.
      if (!isRowOverride(group) && !isMultiHierarchyRowOverride(group)) return [];
      return Object.values(group.columnGroups);
    })
  ].flat();
  const lensIds = new Set<string>();

  columnGroupGroups.forEach((group) => {
    // Handle undefined case
    if (!group || isCalculationExpression(group) || isMultiHierarchyCalculatedColumnGroup(group)) return;
    const lensId = group.lens.id;
    lensIds.add(lensId);
  });

  return lensIds;
}

export function getResourceIdsFromReport(report: IAccountSummary): Set<string> {
  const resourceIds = new Set<string>();

  const columnGroups = [...report.columnGroups];

  report.rowGroups.forEach((rowGroup) => {
    if ('grouping' in rowGroup && !isMultiHierarchyRowGroupLike(rowGroup)) {
      resourceIds.add(rowGroup.showOnly.dimension.id);

      rowGroup.grouping.forEach((grouping) => {
        if ('dimension' in grouping) {
          resourceIds.add(grouping.dimension.id);
        }
      });
    }

    if ('columnGroups' in rowGroup) {
      columnGroups.push(...Object.values(rowGroup.columnGroups));
    }
  });

  columnGroups.forEach((columnGroup) => {
    if (isCalculationExpression(columnGroup) || isMultiHierarchyCalculatedColumnGroup(columnGroup)) {
      return;
    }
    resourceIds.add(columnGroup.lens.id);

    columnGroup.grouping.forEach((grouping) => {
      if ('dimension' in grouping) {
        resourceIds.add(grouping.dimension.id);
      }
    });
  });

  return resourceIds;
}

export function generateColumnLabel(columnGroupName?: string | null, columnValue?: string | null): string {
  //Return empty string if the column group is hidden header indicator
  const trimmedGroupName = columnGroupName?.trim() === HiddenColumnGroupIndicator ? '' : columnGroupName?.trim();
  const trimmedValue = columnValue?.trim();
  const hasGroup = !!trimmedGroupName;
  const hasValue = !!trimmedValue;

  if (hasGroup && hasValue) return `${trimmedGroupName} - ${trimmedValue}`;
  if (hasGroup) return trimmedGroupName;
  if (hasValue) return trimmedValue;
  return '';
}

export function getCustomColumnName<T extends IColumnSource>(columnGroup: T, columnValue?: string | null): string {
  if (columnValue === undefined) return null;
  const asDataSource = columnGroup as unknown as IColumnDataSource | MultiHierarchyColumnDataSource;
  const mapped = getColumnValueMapping(asDataSource, columnValue as ColumnValueSegment);
  if (mapped) return mapped;
  // legacy fallback: flat lookup directly on columnValueMapping (pre-segmentId structure)
  if (!asDataSource?.columnValueMapping || columnValue === null) return null;
  const legacyValue = asDataSource.columnValueMapping[columnValue];
  return typeof legacyValue === 'string' ? legacyValue : null;
}

export function getColumnLabel(
  columnGroup: IColumnSource,
  // Even though these are separate arguments, they should be passed together,
  // and in fact, if columnValue is provided, the code expects formattedColumnValue.
  // Both come from the columnValues response, so this shouldn't be a problem.
  columnValue?: string | null,
  formattedColumnValue?: string
): string {
  const columnGroupName = getColumnGroupName(columnGroup);

  // Calculated columns (AS expression and MH calculated) have no per-value sub-label; their label
  // is just the column name/alias. Without this, an MH calc column would render its placeholder
  // value (e.g. "Calculation Column 1 - Empty Values").
  if (isCalculationExpression(columnGroup) || isMultiHierarchyCalculatedColumnGroup(columnGroup)) {
    return columnGroupName;
  }

  const customColumnName = getCustomColumnName(columnGroup, columnValue);
  return generateColumnLabel(columnGroupName, customColumnName || formattedColumnValue);
}

/**
 * One organize-by level's display label: the rename configured at that depth, else the level's own
 * text, else its raw id. Empty when the level carries nothing.
 *
 * The rename is looked up with the path truncated to `level`, because renames are keyed
 * `columnValueMapping[levelField][pathKey]` — an ancestor's rename is not found by the full path,
 * and a descendant's is not found by the level-1 value.
 */
export function getColumnLevelLabel(
  columnGroup: IColumnSource | undefined,
  columnValue: ColumnValue,
  level: number
): string {
  const pathIds = extractColumnValueIds(columnValue).slice(0, level);

  if (columnGroup) {
    const asDataSource = columnGroup as unknown as IColumnDataSource | MultiHierarchyColumnDataSource;
    const renamed = getColumnValueMapping(asDataSource, pathIds.length > 0 ? pathIds : null, level);
    if (renamed) return renamed;
  }

  const ownLabel = columnValue[labelSlot(level)];
  if (ownLabel != null && String(ownLabel) !== '') return String(ownLabel);
  const ownId = columnValue[idSlot(level)];
  return ownId == null ? '' : String(ownId);
}

/**
 * Every organize-by level of a column's path, outermost first, joined for a single-line surface.
 *
 * Whenever a column group is organized by more than one level it repeats its leaf labels under each
 * parent, so a label built from the leaf alone names several different columns identically. This is
 * for the surfaces that have one line to spend — the chart series pickers and the calculation
 * formula picker; the grid shows the same path structurally instead.
 *
 * A one-level column returns its bare label, so single-level reports are unaffected.
 */
export function getColumnPathLabel(columnGroup: IColumnSource | undefined, columnValue: ColumnValue): string {
  const levels = Math.max(columnValueLevelCount(columnValue), 1);
  const labels: string[] = [];
  for (let level = 1; level <= levels; level++) {
    labels.push(getColumnLevelLabel(columnGroup, columnValue, level));
  }
  return labels.filter((label) => label !== '').join(COLUMN_LEVEL_SEPARATOR);
}

/**
 * A column's label wherever a series is named by one line of text — the chart series pickers and
 * the chart legends they have to agree with: the column group name followed by the column's whole
 * organize-by path.
 *
 * The tuple-taking counterpart of {@link getColumnLabel}, which takes a single value/label pair and
 * so can only ever describe level 1 — every deep column of one group came out reading the same.
 */
export function getColumnLabelForValue(columnGroup: IColumnSource, columnValue: ColumnValue): string {
  const columnGroupName = getColumnGroupName(columnGroup);

  // Calculated columns carry no per-value sub-label; their label is the column name/alias alone.
  if (isCalculationExpression(columnGroup) || isMultiHierarchyCalculatedColumnGroup(columnGroup)) {
    return columnGroupName;
  }

  return generateColumnLabel(columnGroupName, getColumnPathLabel(columnGroup, columnValue));
}

export function getColumnGroupsById(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary
): Record<string, IColumnSource> {
  return keyBy(accountSummary?.columnGroups ?? [], 'id');
}
